import { useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Loader2, Copy, Power } from 'lucide-react';
import { Link } from 'react-router-dom';

const OB = { verified: ['Verified', 'bg-emerald-100 text-emerald-700'], under_review: ['Under Review', 'bg-blue-100 text-blue-700'],
  rejected: ['Rejected', 'bg-rose-100 text-rose-700'], pending: ['KYC / Exam Pending', 'bg-amber-100 text-amber-700'] };
export const onboardingBadge = (s) => {
  const [l, c] = OB[s] || OB.pending;
  return <Badge className={`border-0 text-[11px] ${c}`} data-testid="onboarding-badge">{l}</Badge>;
};

const TH = 'text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const EMPTY = { name: '', email: '', phone: '', password: '' };

function CreateDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post('/sales/persons', form);
      toast.success(`Sales person created · ${data.sales_code}`);
      setForm(EMPTY); onDone(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-sales-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Sales Person</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[['name', 'Full Name', 'text'], ['email', 'Login Email', 'email'], ['phone', 'Phone', 'text'], ['password', 'Password (min 6)', 'text']].map(([k, l, t]) => (
            <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
              <Input type={t} value={form[k]} required={k !== 'phone'} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`sales-form-${k}`} /></div>
          ))}
          <p className="text-xs text-brand-text-secondary">A code like SP-001 is generated automatically.</p>
          <Button type="submit" disabled={saving} className="w-full bg-brand-primary text-white" data-testid="sales-form-submit">
            {saving ? <Loader2 size={15} className="animate-spin" /> : 'Create Sales Person'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SalesPersonsTab({ people, reload }) {
  const [open, setOpen] = useState(false);
  const toggle = async (p) => {
    try {
      await api.put(`/sales/persons/${p.id}`, { name: p.name, phone: p.phone || '', status: p.status === 'active' ? 'inactive' : 'active' });
      toast.success('Status updated'); reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const copyLink = (code) => { navigator.clipboard.writeText(`${window.location.origin}/register?sp=${code}`); toast.success('Registration link copied'); };
  return (
    <Card className="border border-brand-border bg-white">
      <div className="p-3 flex justify-end border-b border-brand-border">
        <Button onClick={() => setOpen(true)} className="bg-brand-primary text-white h-9 text-sm" data-testid="add-sales-person-btn"><Plus size={15} className="mr-1" /> Add Sales Person</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sales-persons-table">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Code', 'Name', 'Login', 'Clients', 'Pending', 'Paid', 'Status', 'Actions'].map(h => <th key={h} className={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {people.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No sales persons yet</td></tr> : people.map(p => (
              <tr key={p.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                <td className="py-3 px-4 font-mono text-xs text-brand-primary font-semibold">{p.sales_code}</td>
                <td className="py-3 px-4 font-medium"><Link to={`/sales-team/${p.id}`} className="hover:text-brand-primary hover:underline" data-testid={`sp-name-${p.sales_code}`}>{p.name}</Link><p className="text-xs text-brand-text-secondary">{p.phone}</p></td>
                <td className="py-3 px-4 text-xs">{p.email}</td>
                <td className="py-3 px-4 font-semibold" data-testid={`sp-client-count-${p.sales_code}`}>{p.client_count}</td>
                <td className="py-3 px-4 text-amber-700">{money(p.commission_pending)}</td>
                <td className="py-3 px-4 text-emerald-700">{money(p.commission_paid)}</td>
                <td className="py-3 px-4"><div className="flex flex-col gap-1 items-start">{onboardingBadge(p.onboarding_status)}<Badge className={`border-0 text-[10px] ${p.status === 'active' ? 'bg-slate-100 text-slate-600' : 'bg-slate-300 text-slate-700'}`}>{p.status}</Badge></div></td>
                <td className="py-3 px-4 flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copyLink(p.sales_code)} data-testid={`copy-link-${p.sales_code}`}><Copy size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggle(p)} data-testid={`toggle-sp-${p.sales_code}`}><Power size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateDialog open={open} onClose={() => setOpen(false)} onDone={reload} />
    </Card>
  );
}
